// Environment configuration interface
interface EnvironmentConfig {
    IdentityBaseUrl: string;
    ClientId: string;
    EnvironmentLabel: string;
    SignalRUrl: string;
    WebApiUrl: string;
    CompanionUrl: string;
    RegionTypeDescription: string[];
    HideCameraImages: boolean;
}

// Keys of ENVIRONMENT_CONFIG. Naming these ties the matcher table and the config table together, so
// a matcher pointing at an environment that has no config entry is a compile error rather than an
// undefined lookup at runtime.
type EnvKey = "DEV" | "QAT" | "UAT" | "STG" | "PROD" | "UAT_AUS" | "PROD_AUS";

// Web hostname matchers - order matters (first match wins)
const HOST_MATCHERS: Array<{ match: (host: string, port: string) => boolean; env: EnvKey }> = [
    { match: (h) => h.startsWith("manage-uat.") && h.endsWith(".auggie.airprodiag.com.au"), env: "UAT_AUS" },
    { match: (h) => h.endsWith(".auggie.airprodiag.com.au"), env: "PROD_AUS" },
    { match: (h) => h.startsWith("manage-qat."), env: "QAT" },
    { match: (h) => h.startsWith("manage-uat."), env: "UAT" },
    { match: (h) => h.startsWith("manage-stg."), env: "STG" },
    { match: (h) => h.startsWith("manage."), env: "PROD" },
    { match: (h, p) => h === "localhost" && p === "44342", env: "DEV" },
];

// Environment configuration table
const ENVIRONMENT_CONFIG: Record<EnvKey, EnvironmentConfig> = {
    DEV: {
        IdentityBaseUrl: "https://localhost:5001",
        ClientId: "auggie-spa-management-dev",
        EnvironmentLabel: "Development Environment",
        SignalRUrl: "http://localhost:7174/api",
        WebApiUrl: "https://localhost:44358/",
        CompanionUrl: "https://localhost:44343/",
        RegionTypeDescription: ["North America", "Europe"],
        HideCameraImages: false,
    },
    QAT: {
        IdentityBaseUrl: "https://auth-qat.airprodiag.com",
        ClientId: "auggie-spa-management-qat",
        EnvironmentLabel: "Quality Assurance Testing Environment",
        SignalRUrl: "https://api-qat.auggie.tech/signalr",
        WebApiUrl: "https://api-qat.auggie.tech/manage/",
        CompanionUrl: "https://companion-qat.auggie.tech/",
        RegionTypeDescription: ["North America", "Europe"],
        HideCameraImages: false,
    },
    UAT: {
        IdentityBaseUrl: "https://auth-uat.airprodiag.com",
        ClientId: "auggie-spa-management-uat",
        EnvironmentLabel: "User Acceptance Testing Environment",
        SignalRUrl: "https://api-uat.auggie.tech/signalr",
        WebApiUrl: "https://api-uat.auggie.tech/manage/",
        CompanionUrl: "https://companion-uat.auggie.tech/",
        RegionTypeDescription: ["North America", "Europe"],
        HideCameraImages: false,
    },
    STG: {
        IdentityBaseUrl: "https://auth-stg.airprodiag.com",
        ClientId: "auggie-spa-management-stg",
        EnvironmentLabel: "",
        SignalRUrl: "https://api-stg.auggie.tech/signalr",
        WebApiUrl: "https://api-stg.auggie.tech/manage/",
        CompanionUrl: "https://companion-stg.auggie.tech/",
        RegionTypeDescription: ["North America", "Europe"],
        HideCameraImages: false,
    },
    PROD: {
        IdentityBaseUrl: "https://auth.airprodiag.com",
        ClientId: "auggie-spa-management-prd",
        EnvironmentLabel: "",
        SignalRUrl: "https://api.auggie.tech/signalr",
        WebApiUrl: "https://api.auggie.tech/manage/",
        CompanionUrl: "https://companion.auggie.tech/",
        RegionTypeDescription: ["North America", "Europe"],
        HideCameraImages: false,
    },
    UAT_AUS: {
        IdentityBaseUrl: "https://auth-uat.airprodiag.com.au",
        ClientId: "auggieaus-spa-management-uat",
        EnvironmentLabel: "User Acceptance Testing Environment",
        SignalRUrl: "https://api-uat.auggie.airprodiag.com.au/signalr",
        WebApiUrl: "https://api-uat.auggie.airprodiag.com.au/manage/",
        CompanionUrl: "https://companion-uat.auggie.airprodiag.com.au/",
        RegionTypeDescription: ["Australia", "Europe"],
        HideCameraImages: true,
    },
    PROD_AUS: {
        IdentityBaseUrl: "https://auth.airprodiag.com.au",
        ClientId: "auggieaus-spa-management-prd",
        EnvironmentLabel: "",
        SignalRUrl: "https://api.auggie.airprodiag.com.au/signalr",
        WebApiUrl: "https://api.auggie.airprodiag.com.au/manage/",
        CompanionUrl: "https://companion.auggie.airprodiag.com.au/",
        RegionTypeDescription: ["Australia", "Europe"],
        HideCameraImages: true,
    },
};

export class Globals {

    private static cachedEnvKey: EnvKey | null = null;

    private static getEnvKey(): EnvKey {
        if (this.cachedEnvKey) return this.cachedEnvKey;

        const host = globalThis.location.hostname;
        const port = globalThis.location.port;
        for (const { match, env } of HOST_MATCHERS) {
            if (match(host, port)) {
                this.cachedEnvKey = env;
                return env;
            }
        }
        throw new Error(`Unknown host: ${host}`);
    }

    private static getConfig(): EnvironmentConfig {
        return ENVIRONMENT_CONFIG[this.getEnvKey()];
    }

    public static GetEnvironment(): string {
        return this.getConfig().EnvironmentLabel;
    }

    public static GetSignalRFunction(): string {
        return this.getConfig().SignalRUrl;
    }

    public static GetWebApi(): string {
        return this.getConfig().WebApiUrl;
    }

    public static GetCompanionUrl(): string {
        return this.getConfig().CompanionUrl;
    }

    public static GetRegionTypeDescription(): string[] {
        return this.getConfig().RegionTypeDescription;
    }

    public static GetHideCameraImages(): boolean {
        return this.getConfig().HideCameraImages;
    }

    public static IdentityUrl(url: string): string {
        return this.getConfig().IdentityBaseUrl + url;
    }

    public static GetIdentityClientId(): string {
        return this.getConfig().ClientId;
    }
}
export default Globals;
